import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AddAttachmentComponent } from './add-attachment/add-attachment.component';
import { DrawerService } from '../../../../core/services/drawer.service';
import { StudentAttachmentService } from './student-attachment.service';
import { finalize, forkJoin, Observable, of, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import {
  Attachment,
  CaseDetailModel,
  ManageStudentDrawerParams,
  SelectedFolder,
} from '../../insights.interface';
import { AttachmentMode, InsightMode } from '../../insights.enums';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoaderService } from '../../../../core/services/loader.service';
import { CommonService } from '../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../core/models/error.models';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { AttachmentFolderService } from './attachment-folder.service';
import { ApiResponse } from '../../../../core/models/api-response.models';
import { NzIconModule } from 'ng-zorro-antd/icon';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { ManageSettingsService } from '../../../manage-settings/manage-settings.service';
import { QuestionnairesFormComponent } from './questionnaires-form/questionnaires-form.component';
import { QuestionnaireMode } from './questionnaires-form/questionnaires.interface';

interface AttachmentWithSelection {
  id?: number;
  title: string;
  folders: SelectedFolder[];
}

@Component({
  selector: 'app-insights-admin',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    CommonModule,
    NzSpinModule,
    NzEmptyModule,
    ConfirmDialogComponent,
    NzCheckboxModule,
    FormsModule,
    NzCheckboxModule,
    NzInputModule,
    NzIconModule,
    NzFormModule,
    FormsModule,
    NzCollapseModule,
    ReactiveFormsModule,
  ],
  templateUrl: './insights-admin.component.html',
  styleUrl: './insights-admin.component.scss',
})
export class InsightsAdminComponent implements OnInit, OnDestroy {
  private studentAttachmentService = inject(StudentAttachmentService);
  private loaderService = inject(LoaderService);

  generalAttachmentData: AttachmentWithSelection | null = null;
  planData: AttachmentWithSelection[] = [];
  folders: SelectedFolder[] = [];
  hasAttachments = false;

  private destroy$ = new Subject<void>();

  loading$: Observable<boolean> = of(false);

  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.VIEW,
  };

  readonly atttachmentMode = AttachmentMode;
  readonly questionnaireMode = QuestionnaireMode;
  showConfirmDialog = false;
  isDeleting = false;
  attachmentId: null | number = null;
  confrimDialogMsg = 'Are you sure you want to delete this attachment?';
  isAddFolderOpen = false;
  selectAll = false;
  folderForm!: FormGroup;
  selectedPanel: boolean[] = [];
  titlePattern = new RegExp('^\\S(.*\\S)?$');

  constructor(
    private drawerService: DrawerService,
    private commonService: CommonService,
    private folderSrv: AttachmentFolderService,
    private manageSettingsService: ManageSettingsService,
    private fb: FormBuilder
  ) {
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.resetComponentState();
    this.loadPlansWithAttachments();
  }

  loadPlansWithAttachments(): void {
    this.loaderService.show();
    const caseId = this.caseParams.case?.id;
    if (caseId) {
      forkJoin({
        plans: this.manageSettingsService.getAllPlans(
          this?.caseParams?.case?.student?.organizationId ?? 0
        ),
        attachmentsResponse:
          this.studentAttachmentService.getAttachmentsWithPlanTypeId(caseId),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ plans, attachmentsResponse }) => {
            // --- Normalize Plans ---
            const planList = Array.isArray(plans.result)
              ? plans.result
              : plans.result.items || [];
            this.planData = [];

            const allAttachmentGroups = attachmentsResponse.result.data || [];

            const generalAttachments = allAttachmentGroups
              .filter(r => r.planTypeId === 0)
              .flatMap(item =>
                item.attachments.map(att => ({
                  ...att,
                  selected: false,
                  folderId: att.folderId ?? 1,
                  folderName: att.folderName || 'Default',
                }))
              );

            const groupedGeneral = this.groupByFolder(generalAttachments);

            this.generalAttachmentData = {
              title: 'General Attachments',
              folders: groupedGeneral,
            };

            planList.map(plan => {
              const related = allAttachmentGroups
                .filter(r => r.planTypeId === plan.id)
                .flatMap(item =>
                  item.attachments.map(att => ({
                    ...att,
                    selected: false,
                    folderId: att.folderId ?? 1,
                    folderName: att.folderName || 'Default',
                  }))
                );

              const grouped = this.groupByFolder(related);

              this.planData.push({
                title: plan.name,
                id: plan.id,
                folders: grouped,
              });
            });
            this.resetComponentState();
            this.loaderService.hide();
          },

          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
            this.resetComponentState();
            this.loaderService.hide();
          },
        });
    }
  }

  groupByFolder(attachments: Attachment[]): SelectedFolder[] {
    const grouped = groupBy(attachments, 'folderId');
    return map(grouped, (atts, folderId) => ({
      folderId: Number(folderId),
      folderName: atts[0].folderName || '',
      selected: false,
      active: true,
      attachments: atts,
    }));
  }

  onFolderExpand(folders: SelectedFolder[], index: number, isActive: boolean) {
    folders[index].active = isActive;
  }

  selectedCount(folders: SelectedFolder[]): number {
    return (
      folders.reduce((count, folder) => {
        const selectedInFolder =
          folder.attachments?.filter((att: Attachment) => att.selected)
            ?.length || 0;
        return count + selectedInFolder;
      }, 0) || 0
    );
  }

  onSelectAllChange(folders: SelectedFolder[]): void {
    folders.forEach(folder => {
      folder.selected = this.selectAll;
      folder.attachments.forEach(
        (att: Attachment) => (att.selected = this.selectAll)
      );
    });
  }

  onSelectFolderChange(folders: SelectedFolder[], folderId: number): void {
    const folder = folders.find(f => f.folderId === folderId);
    if (!folder) return;

    folder.attachments.forEach(
      att => (att.selected = folder.selected || false)
    );
    this.updateSelectAllState(folders);
  }

  onAttachmentSelectionChange(folders: SelectedFolder[]): void {
    folders.forEach(folder => {
      folder.selected = folder.attachments.every(att => att.selected);
    });
    this.updateSelectAllState(folders);
  }

  updateSelectAllState(folders: SelectedFolder[]): void {
    this.selectAll = folders.every(
      folder => folder.selected && folder.attachments.every(att => att.selected)
    );
  }

  private resetSelectionState(): void {
    this.selectAll = false;

    this.generalAttachmentData?.folders?.forEach(folder => {
      folder.selected = false;
      folder.attachments.forEach(att => (att.selected = false));
    });

    this.planData?.forEach(plan => {
      plan.folders?.forEach(folder => {
        folder.selected = false;
        folder.attachments.forEach(att => (att.selected = false));
      });
    });
  }

  private resetComponentState(): void {
    this.resetSelectionState();
    this.showConfirmDialog = false;
    this.isDeleting = false;
    this.attachmentId = null;
  }

  downloadSelectedAttachments(item: AttachmentWithSelection): void {
    const selectedAttachments =
      item.folders?.flatMap(folder =>
        folder.attachments.filter((att: Attachment) => att.selected)
      ) || [];

    if (selectedAttachments.length === 0) {
      this.commonService.notify(
        'warning',
        'Please select at least one attachment to download'
      );
      return;
    }

    this.loaderService.show();
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const zipFileName = `${studentName}__${item.title}_selected_${Date.now()}.zip`;

    this.studentAttachmentService
      .downloadAttachmentsAsZip(selectedAttachments, zipFileName)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Selected attachments downloaded successfully'
          );
          this.resetSelectionState();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  downloadAllAttachments(section: AttachmentWithSelection): void {
    const allAttachments =
      section.folders?.flatMap(folder => folder.attachments) || [];

    if (allAttachments.length === 0) {
      this.commonService.notify(
        'warning',
        'No attachments available to download'
      );
      return;
    }

    this.loaderService.show();
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const zipFileName = `${studentName}_${section.title}_all_${Date.now()}.zip`;

    this.studentAttachmentService
      .downloadAttachmentsAsZip(allAttachments, zipFileName)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'All attachments downloaded successfully'
          );
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  trackByAttachmentId(index: number, attachment: Attachment): number {
    return attachment.id;
  }

  downloadFile(attachment: Attachment): void {
    window.open(attachment.fileUrl, '_blank');
  }

  openAddAttachmentComponentDrawer(
    selectedPlan: AttachmentWithSelection
  ): void {
    this.drawerService
      .openDrawer(
        AddAttachmentComponent,
        'Add Attachment',
        520,
        true,
        undefined,
        {
          mode: this.atttachmentMode.CREATE,
          attachment: null,
          planTypeId: selectedPlan.id || 0,
          case: this.caseParams.case,
        },
        DRAWER_IDS.STUDENT_ATTACHMENT
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.planData = [];
        this.loadPlansWithAttachments();
      });
  }

  openAttemptQuestionnaireDrawer(
    selectedPlan: AttachmentWithSelection,
    attachment?: Attachment
  ): void {
    this.drawerService
      .openDrawer(
        QuestionnairesFormComponent,
        'Attempt Questionnaire',
        1170,
        true,
        undefined,
        {
          mode: this.questionnaireMode.CREATE,
          planTypeId: selectedPlan.id || 0,
          case: this.caseParams.case,
          attachment: attachment,
        },
        DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.planData = [];
        this.loadPlansWithAttachments();
      });
  }

  viewAttachmentDetails(attachment: Attachment, planTypeId?: number): void {
    if (!attachment.isQuestionnairePDF) {
      this.drawerService
        .openDrawer(
          AddAttachmentComponent,
          'View Attachment',
          520,
          true,
          undefined,
          {
            attachment,
            mode: this.atttachmentMode.VIEW,
            planTypeId,
            case: this.caseParams.case,
          },
          DRAWER_IDS.STUDENT_ATTACHMENT
        )
        .afterClose.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.planData = [];
          this.loadPlansWithAttachments();
        });
    } else {
      this.drawerService
        .openDrawer(
          QuestionnairesFormComponent,
          'Attempt Questionnaire',
          1170,
          true,
          undefined,
          {
            mode: this.questionnaireMode.EDIT,
            planTypeId: planTypeId || 0,
            case: this.caseParams.case,
            attachment: attachment,
          },
          DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
        )
        .afterClose.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.planData = [];
          this.loadPlansWithAttachments();
        });
    }
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageStudentDrawerParams>(DRAWER_IDS.BIP_STUDENT_ATTACHMENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.folderForm = this.fb.group({
            caseId: this.caseParams?.case?.id,
            name: [
              null,
              [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(this.titlePattern),
              ],
            ],
          });
        }
      });
  }

  onDelete(id: number): void {
    this.showConfirmDialog = true;
    this.attachmentId = id;
  }

  handleDelete(): void {
    if (!this.attachmentId) return;

    this.isDeleting = true;
    this.studentAttachmentService
      .deleteAttachment(this.attachmentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isDeleting = false;
          this.showConfirmDialog = false;
          this.attachmentId = null;
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Attachment deleted successfully'
          );
          this.loadPlansWithAttachments();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.resetComponentState();
        },
      });
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.attachmentId = null;
  }

  onSaveFolder() {
    if (this.folderForm.invalid) {
      this.folderForm.get('name')?.markAsTouched();
      return;
    }
    this.folderSrv
      .createFolder(this.folderForm.value)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (res: ApiResponse<boolean>) => {
          if (res.result) {
            this.commonService.notify('success', 'Folder created successfully');
            this.isAddFolderOpen = false;
            this.folderForm.get('name')?.reset();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages[0] || message);
        },
      });
  }

  onCancelFolder() {
    this.isAddFolderOpen = false;
    this.folderForm.get('name')?.reset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
